import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { BRAND_NAME, ROUTES } from '../routes';
import { useAuthStore } from '../store/authStore';
import { selectCartCount, useCartStore } from '../store/cartStore';
const NAV_LINKS = [
    {
        label: 'Home',
        to: '/'
    },
    {
        label: 'Shop',
        to: '/shop'
    },
    {
        label: 'About',
        to: '/about'
    },
    {
        label: 'Contact',
        to: '/contact'
    }
];
export default function Layout() {
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { isAuthenticated, username, isAdmin, logout } = useAuthStore();
    const cartCount = useCartStore(selectCartCount);
    const throbKey = useCartStore((s)=>s.throbKey);
    const [cartThrob, setCartThrob] = useState(false);
    const initialThrobKey = useRef(throbKey);
    useEffect(()=>{
        if (throbKey > initialThrobKey.current) {
            setCartThrob(true);
            const timer = setTimeout(()=>setCartThrob(false), 1000);
            return ()=>clearTimeout(timer);
        }
    }, [
        throbKey
    ]);
    // Reset scroll on every storefront route change. SPA navigation otherwise
    // preserves scrollY across pages, which lands users halfway down a PDP after
    // clicking a catalog row that was at the bottom of the viewport. Triggers on
    // pathname changes only — hash/query updates leave the user where they are.
    // Admin routes use AdminLayout and aren't covered here on purpose; their
    // pages are short.
    //
    // pathname is referenced inside the effect (in the data-route attr write) so
    // Biome's exhaustive-deps trace sees an honest dependency, not a "trigger
    // only" dep that the rule flags.
    const pathname = location.pathname;
    useEffect(()=>{
        document.documentElement.dataset.route = pathname;
        window.scrollTo(0, 0);
    }, [
        pathname
    ]);
    const handleLogout = ()=>{
        logout();
        navigate(ROUTES.login);
    };
    const navLinkCls = (isActive)=>`relative px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.28em] transition-colors ${isActive ? 'text-obsidian-900' : 'text-obsidian-600 hover:text-obsidian-900'}`;
    const accountLinkCls = 'px-2 py-1.5 text-[11px] font-medium uppercase tracking-[0.24em] text-obsidian-600 hover:text-obsidian-900 transition-colors';
    return /*#__PURE__*/ _jsxs("div", {
        className: "min-h-screen bg-bone-50 flex flex-col",
        children: [
            /*#__PURE__*/ _jsxs("nav", {
                className: "sticky top-0 z-50 bg-bone-50/85 backdrop-blur-md border-b border-dune-200/60",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8",
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "flex justify-between h-16 items-center",
                            children: [
                                /*#__PURE__*/ _jsx(Link, {
                                    to: "/",
                                    className: "font-display italic text-2xl text-obsidian-900 hover:text-clay-600 transition-colors",
                                    style: {
                                        fontWeight: 500,
                                        letterSpacing: '-0.02em',
                                        fontVariationSettings: '"opsz" 96, "SOFT" 100'
                                    },
                                    children: BRAND_NAME
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "hidden lg:flex items-center gap-1",
                                    children: NAV_LINKS.map((link)=>{
                                        const isActive = location.pathname === link.to;
                                        return /*#__PURE__*/ _jsxs(Link, {
                                            to: link.to,
                                            className: navLinkCls(isActive),
                                            children: [
                                                link.label,
                                                isActive && /*#__PURE__*/ _jsx("span", {
                                                    className: "absolute left-3 right-3 bottom-1 h-px bg-clay-500",
                                                    "aria-hidden": true
                                                })
                                            ]
                                        }, link.to);
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "hidden sm:flex items-center gap-1",
                                    children: [
                                        isAuthenticated ? /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(Link, {
                                                    to: ROUTES.accountOrders,
                                                    className: accountLinkCls,
                                                    children: "Orders"
                                                }),
                                                isAdmin && /*#__PURE__*/ _jsx(Link, {
                                                    to: ROUTES.admin,
                                                    className: accountLinkCls,
                                                    children: "Admin"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "px-2 text-[11px] text-obsidian-500 font-mono",
                                                    children: username
                                                }),
                                                /*#__PURE__*/ _jsx("button", {
                                                    type: "button",
                                                    onClick: handleLogout,
                                                    className: accountLinkCls,
                                                    children: "Logout"
                                                })
                                            ]
                                        }) : /*#__PURE__*/ _jsx(Link, {
                                            to: ROUTES.login,
                                            className: accountLinkCls,
                                            children: "Sign In"
                                        }),
                                        /*#__PURE__*/ _jsxs(Link, {
                                            to: ROUTES.cart,
                                            className: "relative p-2 text-obsidian-700 hover:text-obsidian-900 transition-colors",
                                            "aria-label": "Cart",
                                            children: [
                                                /*#__PURE__*/ _jsx("svg", {
                                                    className: "w-5 h-5",
                                                    fill: "none",
                                                    viewBox: "0 0 24 24",
                                                    stroke: "currentColor",
                                                    "aria-hidden": "true",
                                                    children: /*#__PURE__*/ _jsx("path", {
                                                        strokeLinecap: "round",
                                                        strokeLinejoin: "round",
                                                        strokeWidth: 1.5,
                                                        d: "M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                                                    })
                                                }),
                                                cartCount > 0 && /*#__PURE__*/ _jsx("span", {
                                                    className: `absolute -top-0.5 -right-0.5 bg-clay-500 text-bone-50 text-[10px] font-mono font-medium w-4.5 h-4.5 flex items-center justify-center rounded-full ${cartThrob ? 'animate-pulse' : ''}`,
                                                    children: cartCount > 9 ? '9+' : cartCount
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-2 sm:hidden",
                                    children: [
                                        /*#__PURE__*/ _jsxs(Link, {
                                            to: ROUTES.cart,
                                            className: "relative p-2 text-obsidian-700",
                                            "aria-label": "Cart",
                                            children: [
                                                /*#__PURE__*/ _jsxs("svg", {
                                                    className: "w-5 h-5",
                                                    fill: "none",
                                                    viewBox: "0 0 24 24",
                                                    stroke: "currentColor",
                                                    "aria-hidden": "true",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("title", {
                                                            children: "Cart"
                                                        }),
                                                        /*#__PURE__*/ _jsx("path", {
                                                            strokeLinecap: "round",
                                                            strokeLinejoin: "round",
                                                            strokeWidth: 1.5,
                                                            d: "M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                                                        })
                                                    ]
                                                }),
                                                cartCount > 0 && /*#__PURE__*/ _jsx("span", {
                                                    className: "absolute -top-0.5 -right-0.5 bg-clay-500 text-bone-50 text-[10px] font-mono font-medium w-4 h-4 flex items-center justify-center rounded-full",
                                                    children: cartCount > 9 ? '9+' : cartCount
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("button", {
                                            type: "button",
                                            onClick: ()=>setMobileMenuOpen(!mobileMenuOpen),
                                            className: "p-2 text-obsidian-700",
                                            "aria-expanded": mobileMenuOpen,
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "sr-only",
                                                    children: "Open menu"
                                                }),
                                                /*#__PURE__*/ _jsxs("svg", {
                                                    className: "h-6 w-6",
                                                    fill: "none",
                                                    viewBox: "0 0 24 24",
                                                    stroke: "currentColor",
                                                    "aria-hidden": "true",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("title", {
                                                            children: "Menu"
                                                        }),
                                                        /*#__PURE__*/ _jsx("path", {
                                                            strokeLinecap: "round",
                                                            strokeLinejoin: "round",
                                                            strokeWidth: 1.5,
                                                            d: mobileMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        })
                    }),
                    mobileMenuOpen && /*#__PURE__*/ _jsxs("div", {
                        className: "sm:hidden border-t border-dune-200/60 bg-bone-50 px-4 py-3 space-y-1",
                        children: [
                            NAV_LINKS.map((link)=>/*#__PURE__*/ _jsx(Link, {
                                    to: link.to,
                                    onClick: ()=>setMobileMenuOpen(false),
                                    className: "block py-2 text-[11px] font-medium uppercase tracking-[0.28em] text-obsidian-600 hover:text-obsidian-900",
                                    children: link.label
                                }, link.to)),
                            /*#__PURE__*/ _jsx("div", {
                                className: "border-t border-dune-200/60 pt-2 mt-2",
                                children: isAuthenticated ? /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "py-2 text-[11px] text-obsidian-500 font-mono",
                                            children: [
                                                "Signed in as ",
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "font-semibold text-obsidian-900",
                                                    children: username
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(Link, {
                                            to: ROUTES.accountOrders,
                                            onClick: ()=>setMobileMenuOpen(false),
                                            className: "block py-2 text-[11px] font-medium uppercase tracking-[0.28em] text-obsidian-600",
                                            children: "My Orders"
                                        }),
                                        isAdmin && /*#__PURE__*/ _jsx(Link, {
                                            to: ROUTES.admin,
                                            onClick: ()=>setMobileMenuOpen(false),
                                            className: "block py-2 text-[11px] font-medium uppercase tracking-[0.28em] text-obsidian-600",
                                            children: "Admin"
                                        }),
                                        /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            onClick: handleLogout,
                                            className: "block w-full text-left py-2 text-[11px] font-medium uppercase tracking-[0.28em] text-obsidian-600",
                                            children: "Logout"
                                        })
                                    ]
                                }) : /*#__PURE__*/ _jsx(Link, {
                                    to: ROUTES.login,
                                    onClick: ()=>setMobileMenuOpen(false),
                                    className: "block py-2 text-[11px] font-medium uppercase tracking-[0.28em] text-obsidian-600",
                                    children: "Sign In"
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("main", {
                className: "flex-1",
                children: /*#__PURE__*/ _jsx(Outlet, {})
            }),
            /*#__PURE__*/ _jsx("footer", {
                className: "bg-obsidian-950 text-bone-200 mt-24",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "grid grid-cols-1 md:grid-cols-3 gap-12",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx(Link, {
                                            to: "/",
                                            className: "font-display italic text-3xl text-bone-50 hover:text-clay-300 transition-colors",
                                            style: {
                                                fontWeight: 500,
                                                letterSpacing: '-0.02em',
                                                fontVariationSettings: '"opsz" 96, "SOFT" 100'
                                            },
                                            children: BRAND_NAME
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "mt-5 text-sm text-dune-300 leading-[1.65] max-w-xs",
                                            children: "A curated catalog of premium smoking accessories. Arizona born, small-batch focused, and designed to elevate the everyday session."
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-[10px] uppercase tracking-[0.3em] text-ember-300/90 font-medium mb-5 font-mono",
                                            children: "Catalog"
                                        }),
                                        /*#__PURE__*/ _jsxs("ul", {
                                            className: "space-y-3 text-sm",
                                            children: [
                                                /*#__PURE__*/ _jsx("li", {
                                                    children: /*#__PURE__*/ _jsx(Link, {
                                                        to: ROUTES.shop,
                                                        className: "hover:text-bone-50 transition-colors",
                                                        children: "All essentials"
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("li", {
                                                    children: /*#__PURE__*/ _jsx(Link, {
                                                        to: "/shop/grinder",
                                                        className: "hover:text-bone-50 transition-colors",
                                                        children: "The Zenith Modular Mill"
                                                    })
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-[10px] uppercase tracking-[0.3em] text-ember-300/90 font-medium mb-5 font-mono",
                                            children: "Company"
                                        }),
                                        /*#__PURE__*/ _jsxs("ul", {
                                            className: "space-y-3 text-sm",
                                            children: [
                                                /*#__PURE__*/ _jsx("li", {
                                                    children: /*#__PURE__*/ _jsx(Link, {
                                                        to: ROUTES.about,
                                                        className: "hover:text-bone-50 transition-colors",
                                                        children: "About"
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("li", {
                                                    children: /*#__PURE__*/ _jsx(Link, {
                                                        to: ROUTES.contact,
                                                        className: "hover:text-bone-50 transition-colors",
                                                        children: "Contact"
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("li", {
                                                    children: /*#__PURE__*/ _jsx(Link, {
                                                        to: ROUTES.orderLookup,
                                                        className: "hover:text-bone-50 transition-colors",
                                                        children: "Order lookup"
                                                    })
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mt-14 pt-6 border-t border-obsidian-700 flex flex-col sm:flex-row justify-between items-center gap-3",
                            children: [
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-xs text-dune-400 font-mono",
                                    children: [
                                        "© ",
                                        new Date().getFullYear(),
                                        " ",
                                        BRAND_NAME
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-[10px] uppercase tracking-[0.3em] text-dune-400 font-mono",
                                    children: "Designed in Arizona · Built to elevate"
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
